import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Database, ArrowRight, Sparkles, MessageSquare } from 'lucide-react';

const DemoDatasetModal = ({ isOpen, onClose, datasets, onSelect }) => {
  const [hoveredDataset, setHoveredDataset] = useState(null);
  
  return (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="w-full max-w-3xl rounded-brand-lg border border-[rgba(32,30,29,0.12)] bg-sand-100 shadow-brand-lg"
          initial={{ scale: 0.96, opacity: 0, y: 10 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.96, opacity: 0, y: 10 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-[rgba(32,30,29,0.12)] p-6">
            <div>
              <h2 className="font-display text-2xl text-ink">Choose a demo dataset</h2>
              <p className="mt-1 text-sm text-sand-700">
                Try DuckPilot instantly with sample data — no signup required.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-2 text-sand-500 hover:bg-sand-200 hover:text-ink transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="p-6">
            <div className="grid gap-4 md:grid-cols-3 mb-6">
              {datasets.map((dataset, index) => {
                const colors = [
                  { border: 'border-bill-300', bg: 'bg-white', icon: 'text-bill-600', hover: 'hover:bg-bill-100' },
                  { border: 'border-sage-300', bg: 'bg-white', icon: 'text-sage-600', hover: 'hover:bg-sage-100' },
                  { border: 'border-sand-300', bg: 'bg-white', icon: 'text-sand-700', hover: 'hover:bg-sand-100' }
                ];
                const color = colors[index % colors.length];

                return (
                  <motion.button
                    key={dataset.id}
                    type="button"
                    onClick={() => onSelect(dataset)}
                    onMouseEnter={() => setHoveredDataset(dataset)}
                    onMouseLeave={() => setHoveredDataset(null)}
                    className={`group rounded-brand-md border ${color.border} ${color.bg} p-5 text-left ${color.hover} transition-all duration-200 shadow-brand-sm`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <Database className={`h-5 w-5 ${color.icon}`} strokeWidth={2.25} />
                      <span className="text-base font-bold text-ink">{dataset.name}</span>
                    </div>
                    <p className="text-xs text-sand-700 mb-4 leading-relaxed">{dataset.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-sand-600 tnum">
                        {dataset.rows} rows • {dataset.columns} cols
                      </div>
                      <ArrowRight className="h-4 w-4 text-sand-400 group-hover:text-bill-600 group-hover:translate-x-1 transition-all" />
                    </div>
                  </motion.button>
                );
              })}
            </div>

            {/* Example Questions Preview */}
            <AnimatePresence mode="wait">
              {hoveredDataset && hoveredDataset.questions && (
                <motion.div
                  key={hoveredDataset.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="rounded-brand-md border border-[rgba(32,30,29,0.12)] bg-white p-4"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <MessageSquare className="h-4 w-4 text-sand-500" />
                    <span className="text-sm font-semibold text-sand-800">Example questions for {hoveredDataset.name}</span>
                  </div>
                  <div className="space-y-2">
                    {hoveredDataset.questions.slice(0, 2).map((question, idx) => (
                      <div key={idx} className="text-xs text-sand-700 flex items-start gap-2">
                        <span className="text-bill-500 mt-0.5">→</span>
                        <span>{question}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div className="border-t border-[rgba(32,30,29,0.12)] px-6 py-4">
            <div className="flex items-center gap-2 text-xs text-sand-600">
              <Sparkles className="h-3 w-3 text-bill-500" />
              <span>Upload and query your own files after creating a free account.</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
  );
};

export default DemoDatasetModal;
